import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Eye, Send, Ban, FileCheck2, AlertTriangle } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { WARN_CHECKSUM, checksumMatches } from "../../lib/cnp";
import { changeRows, changeSource, differsNote } from "../../lib/ministryFields";
import { dossierTitle } from "../../lib/regime";

/**
 * The accreditation-dossier panel inside the enrollment drawer.
 *
 * Its own file rather than more of `EnrollmentsTab.jsx`, which is already
 * close to 2000 lines.
 *
 * Two things here are not styling and should survive edits:
 *
 *   * The CNP arrives masked and is only fetched in full on request, which
 *     the server audits. That is not distrust of operators — it is the number
 *     not sitting on a shared screen or under a shoulder for the length of a
 *     call. While typing one it is visible, because you cannot check what you
 *     cannot see; it goes back to masked on save.
 *   * An existing record is never overwritten from here. What a second
 *     version says differently is stored as a pending change for somebody to
 *     resolve, and the panel says so rather than pretending the save worked.
 */

const FIELD =
  "w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#373A45] " +
  "outline-none focus:border-[#965EA7] transition-colors";

const STATUS_LABEL = {
  missing: { text: "Lipsă", cls: "bg-[#F3F4F6] text-[#6B7280]" },
  email_sent: { text: "Email trimis", cls: "bg-[#E4EFF6] text-[#2E6F94]" },
  by_participant: { text: "Completat de participant", cls: "bg-[#EAF4EE] text-[#2F6B4E]" },
  by_operator: { text: "Completat telefonic", cls: "bg-[#EAF4EE] text-[#2F6B4E]" },
  refused: { text: "Refuză", cls: "bg-[#FDECEC] text-[#9b1f2c]" },
};

const EMPTY = {
  nume: "", initiala_tatalui: "", prenume: "", cnp: "",
  functie_didactica: "", specializare: "", unitate: "", city: "", county: "",
};

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#6B7280] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

// `fped`: a Curs FPED enrolment — the same data, asked for the contract and the certificate.
export default function MinistryDataPanel({ enrollmentId, canMutate, fped = false }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/enrollments/${enrollmentId}/ministry-data`);
      setData(r.data);
      setRevealed(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut încărca datele pentru minister."));
      setData(false);
    }
  }, [enrollmentId]);

  useEffect(() => { load(); }, [load]);

  const startEditing = () => {
    // Seeded from checkout, so an operator on the phone confirms rather than
    // re-types six fields the person already gave us.
    setForm({ ...EMPTY, ...(data?.prefill || {}) });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/enrollments/${enrollmentId}/ministry-data`, form);
      if (r.data?.already_submitted) {
        toast.warning(
          "Datele existau deja. Ce ai completat diferit a fost salvat ca modificare de verificat."
        );
      } else {
        toast.success("Datele au fost salvate.");
      }
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reveal = async () => {
    try {
      const r = await api.get(
        `/admin/enrollments/${enrollmentId}/ministry-data`, { params: { reveal: true } },
      );
      setRevealed(r.data?.record?.cnp || null);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const setRefused = async (refused) => {
    setBusy(true);
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/ministry-data/refuse`, {
        refused, operator_note: data?.operator_note || "",
      });
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // 19.10.2026: a second version of the data blocked the contract with no way out.
  const resolveChange = async (index, action) => {
    setBusy(true);
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/ministry-data/changes/${index}`, { action });
      toast.success(action === "apply" ? "Am aplicat valorile noi." : "Am păstrat datele salvate.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/ministry-data/resend`);
      toast.success("Emailul a fost retrimis.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (data === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B7280] py-4">
        <Loader2 size={14} className="animate-spin" /> Se încarcă…
      </div>
    );
  }
  if (data === false) return null;

  const record = data.record;
  const badge = STATUS_LABEL[data.status] || STATUS_LABEL.missing;
  // The index is the change's place in the full list: that is how the server finds it.
  const pending = (record?.pending_changes || []).map((c, index) => ({ ...c, index })).filter((c) => !c.resolved_at);

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-[#FAFAFB] p-4" data-testid="ministry-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileCheck2 size={16} className="text-[#965EA7]" />
          <span className="font-semibold text-sm text-[#373A45]">{dossierTitle(fped)}</span>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${badge.cls}`}
          data-testid="ministry-panel-status"
        >
          {badge.text}
        </span>
      </div>

      {(data.differs || []).length > 0 && (
        /* The dossier filled in what the enrolment never had; where the two
           disagree the enrolment was left alone, so somebody has to decide
           which is right (25.09.2026). */
        <div
          className="mt-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#92400E]"
          data-testid="ministry-panel-differs"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{differsNote(data.differs)}</span>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        /* The record was filled twice with different answers. Neither version
           was discarded, and neither is right by default — somebody has to
           look. Loud on purpose: a dossier row nobody checked is worse than a
           missing one, because it looks finished. */
        <div
          className="mt-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-xs text-[#92400E]"
          data-testid="ministry-panel-pending"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <strong>{pending.length === 1 ? "O modificare de verificat." : `${pending.length} modificări de verificat.`}</strong>{" "}
              Datele salvate primele au rămas neschimbate. Până alegi, contractul nu se poate trimite.
              {pending.map((c) => (
                <div key={c.index} className="mt-2 rounded-lg bg-white/70 px-2.5 py-2 text-[#373A45]"
                  data-testid={`ministry-change-${c.index}`}>
                  <p className="text-[11px] text-[#6B7280]">
                    Varianta nouă, {changeSource(c)}
                    {c.submitted_at ? `, pe ${new Date(c.submitted_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}` : ""}:
                  </p>
                  <ul className="mt-1 grid gap-0.5">
                    {changeRows(c).map((row) => (
                      <li key={row.field}>
                        <span className="font-semibold">{row.label}:</span>{" "}
                        <span className={row.field === "cnp" ? "font-mono" : ""}>{row.stored}</span> →{" "}
                        <span className={row.field === "cnp" ? "font-mono" : ""}>{row.submitted}</span>
                      </li>
                    ))}
                  </ul>
                  {canMutate && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button type="button" disabled={busy} onClick={() => resolveChange(c.index, "apply")}
                        className="rounded-full bg-[#965EA7] px-3 py-1 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60"
                        data-testid={`ministry-change-apply-${c.index}`}>
                        Aplică valorile noi
                      </button>
                      <button type="button" disabled={busy} onClick={() => resolveChange(c.index, "keep")}
                        className="rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] disabled:opacity-60"
                        data-testid={`ministry-change-keep-${c.index}`}>
                        Păstrează datele salvate
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!editing && record && (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Field label="Nume" value={record.nume} />
          <Field label="Inițiala tatălui" value={record.initiala_tatalui} />
          <Field label="Prenume" value={record.prenume} />
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-[#6B7280]">CNP</dt>
            <dd className="text-[#373A45] flex items-center gap-2">
              <span className="font-mono" data-testid="ministry-panel-cnp">
                {revealed || record.cnp}
              </span>
              {!revealed && (
                <button
                  type="button"
                  onClick={reveal}
                  className="text-[#965EA7] hover:text-[#7A4986]"
                  title="Afișează CNP-ul — accesul este înregistrat"
                  data-testid="ministry-panel-reveal"
                >
                  <Eye size={14} />
                </button>
              )}
            </dd>
          </div>
          <Field label="Funcția didactică" value={record.functie_didactica} />
          <Field label="Specializarea" value={record.specializare} />
          <Field label="Unitatea" value={record.unitate} />
          <Field label="Localitate / Județ" value={[record.city, record.county].filter(Boolean).join(", ")} />
          <Field
            label="Sursa"
            value={
              record.source === "operator"
                ? `Telefonic · ${record.filled_by_email || "—"}`
                : "Completat de participant"
            }
          />
        </dl>
      )}

      {!editing && !record && (
        <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">
          {data.status === "refused"
            ? "Participantul a refuzat să transmită datele."
            : data.email_sent_at
            ? "Emailul a fost trimis, dar datele nu au fost completate încă."
            : "Datele nu au fost încă solicitate."}
        </p>
      )}

      {editing && (
        <div className="mt-3 space-y-3" data-testid="ministry-panel-form">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_5rem_1fr] gap-3">
            <Row label="Nume *">
              <input className={FIELD} value={form.nume}
                     onChange={(e) => setForm({ ...form, nume: e.target.value })} />
            </Row>
            <Row label="Inițiala *">
              <input className={FIELD} maxLength={2} value={form.initiala_tatalui}
                     onChange={(e) => setForm({ ...form, initiala_tatalui: e.target.value })} />
            </Row>
            <Row label="Prenume *">
              <input className={FIELD} value={form.prenume}
                     onChange={(e) => setForm({ ...form, prenume: e.target.value })} />
            </Row>
          </div>
          <Row label="CNP *">
            {/* Visible while being typed: an operator taking one down over the
                phone has to be able to read back what they entered. */}
            <input className={`${FIELD} font-mono`} inputMode="numeric" maxLength={13}
                   value={form.cnp} autoComplete="off"
                   onChange={(e) => setForm({ ...form, cnp: e.target.value })}
                   data-testid="ministry-panel-cnp-input" />
            {/* Worth more here than on the participant's own page: a number
                read out over the phone is further from the ID card than one
                being copied off it. Still only a warning — saving is allowed,
                because the person on the line knows their own CNP. */}
            {checksumMatches(form.cnp) === false && (
              <span
                className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[#92400E] leading-snug"
                data-testid="ministry-panel-cnp-warning"
              >
                <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {WARN_CHECKSUM}
              </span>
            )}
          </Row>
          <Row label="Funcția didactică de predare *">
            <select className={FIELD} value={form.functie_didactica}
                    onChange={(e) => setForm({ ...form, functie_didactica: e.target.value })}
                    data-testid="ministry-panel-functie">
              <option value="">Selectează funcția</option>
              {(data.teaching_roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Row>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row label="Specializarea">
              <input className={FIELD} value={form.specializare}
                     onChange={(e) => setForm({ ...form, specializare: e.target.value })} />
            </Row>
            <Row label="Unitatea de învățământ">
              <input className={FIELD} value={form.unitate}
                     onChange={(e) => setForm({ ...form, unitate: e.target.value })} />
            </Row>
            <Row label="Localitate">
              <input className={FIELD} value={form.city}
                     onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Row>
            <Row label="Județ">
              <input className={FIELD} value={form.county}
                     onChange={(e) => setForm({ ...form, county: e.target.value })} />
            </Row>
          </div>
          <Row label="Notă (opțional)">
            <input className={FIELD} value={form.operator_note || ""}
                   placeholder="ex. verifică diseară și revine"
                   onChange={(e) => setForm({ ...form, operator_note: e.target.value })} />
          </Row>
          <div className="flex gap-2">
            <button
              type="button" onClick={save} disabled={busy}
              className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2"
              data-testid="ministry-panel-save"
            >
              {busy ? "Se salvează…" : "Salvează"}
            </button>
            <button
              type="button" onClick={() => setEditing(false)} disabled={busy}
              className="rounded-full border border-[#E5E7EB] text-[#373A45] text-sm px-4 py-2 hover:bg-white"
            >
              Renunță
            </button>
          </div>
        </div>
      )}

      {canMutate && !editing && (
        <div className="mt-4 flex flex-wrap gap-2">
          {!record && (
            <button
              type="button" onClick={startEditing} disabled={busy}
              className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white text-xs font-semibold px-3 py-1.5"
              data-testid="ministry-panel-fill"
            >
              Completează telefonic
            </button>
          )}
          {!record && (
            <button
              type="button" onClick={resend} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] text-[#373A45] text-xs px-3 py-1.5 hover:bg-white"
              data-testid="ministry-panel-resend"
            >
              <Send size={12} /> {data.email_sent_at ? "Retrimite emailul" : "Trimite emailul"}
            </button>
          )}
          {!record && (
            <button
              type="button" onClick={() => setRefused(data.status !== "refused")} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] text-[#6B7280] text-xs px-3 py-1.5 hover:bg-white"
              data-testid="ministry-panel-refuse"
            >
              <Ban size={12} />
              {data.status === "refused" ? "Anulează refuzul" : "Marchează refuz"}
            </button>
          )}
        </div>
      )}

      {data.operator_note && !editing && (
        <p className="mt-3 text-xs text-[#6B7280]">
          <strong>Notă:</strong> {data.operator_note}
        </p>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-[#6B7280]">{label}</dt>
      <dd className="text-[#373A45]">{value || "—"}</dd>
    </div>
  );
}
